'use client';

import { BrowserProvider, Contract, formatUnits, JsonRpcProvider, JsonRpcSigner, parseUnits, TransactionReceipt } from 'ethers';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AETHERA_ABI, ERC20_ABI } from '@/lib/web3/abi';
import { BSC_TESTNET, CONTRACTS, TOKEN_DECIMALS } from '@/lib/web3/config';
import { getWeb3Error } from '@/lib/web3/errors';
import { TIME } from '@/lib/config/timing';

type PendingTransaction = { wait: () => Promise<TransactionReceipt | null>; hash: string };
type Web3ContextValue = {
  account: string | null;
  connected: boolean;
  connecting: boolean;
  connect: () => Promise<string | null>;
  disconnect: () => void;
  signMessage: (message: string) => Promise<string>;
  miningContract: (withSigner?: boolean) => Promise<Contract>;
  tokenContract: (address: string, withSigner?: boolean) => Promise<Contract>;
  sendTransaction: (label: string, action: (contract: Contract) => Promise<PendingTransaction>) => Promise<TransactionReceipt | null>;
  formatToken: (value: bigint, decimals?: number) => string;
  parseToken: (value: string, decimals?: number) => bigint;
};

const Web3Context = createContext<Web3ContextValue | null>(null);

// Keep read-only contract traffic off the injected wallet RPC. Some wallet
// endpoints return an empty error payload for eth_call, which ethers surfaces
// as "missing revert data" even though connecting the wallet succeeded.
let readProvider: JsonRpcProvider | null = null;

function getReadProvider() {
  if (readProvider) return readProvider;
  if (typeof window === 'undefined') {
    throw new Error('The blockchain read provider is only available in the browser.');
  }

  // Browser reads always use our same-origin proxy. APP_READ_RPC_URL remains
  // the direct upstream used by server processes such as the indexer. Public
  // BSC endpoints are inconsistent when handling JSON-RPC batches and may
  // omit an item, which ethers reports as "missing response for request".
  const rpcUrl = new URL('/api/rpc', window.location.origin).toString();
  readProvider = new JsonRpcProvider(rpcUrl, BSC_TESTNET.chainId, {
    staticNetwork: true,
    // The same-origin proxy fans these batches out into reliable individual
    // upstream calls and reconstructs a complete response for ethers.
    batchMaxCount: 20,
    batchStallTime: 15,
  });
  return readProvider;
}

async function waitForReadProviderBlock(blockNumber: number) {
  // MetaMask's RPC and the dashboard read RPC can be a few blocks apart.
  for (let attempt = 0; attempt < 12; attempt += 1) {
    try {
      if (await getReadProvider().getBlockNumber() >= blockNumber) return;
    } catch {
      // Retry transient read-node failures.
    }
    await new Promise((resolve) => setTimeout(resolve, TIME.walletProviderRetryDelayMs));
  }
}

const transactionTypes: Record<string, string> = {
  'Stake participation': 'stake_participation',
  'Mining claim': 'mining_claim',
  Registration: 'registration',
  Renewal: 'renewal',
  'Membership renewal': 'renewal',
  'Monthly promoter qualification': 'monthly_promoter_qualification',
  'Monthly owner qualification': 'monthly_promoter_qualification',
  'Portfolio sale': 'portfolio_sale',
  'Daily pool claim': 'daily_pool_claim',
  'Weekly pool claim': 'weekly_pool_claim',
  'Monthly pool claim': 'monthly_pool_claim',
  'Daily Reward Pool claim': 'daily_pool_claim',
  'Weekly Reward Pool claim': 'weekly_pool_claim',
  'Monthly Promoter Pool claim': 'monthly_pool_claim',
};

async function persistConfirmedTransaction(txHash: string, label: string) {
  const response = await fetch('/api/transactions', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ txHash, type: transactionTypes[label] ?? 'contract_transaction' }),
  });
  if (!response.ok) {
    const result = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(result?.error ?? 'Unable to save the confirmed transaction to MySQL.');
  }
}

async function switchToConfiguredNetwork() {
  if (!window.ethereum) throw new Error('MetaMask or a compatible wallet is required.');

  const addOrUpdateNetwork = () => window.ethereum!.request({
    method: 'wallet_addEthereumChain',
    params: [{
      chainId: BSC_TESTNET.chainIdHex,
      chainName: BSC_TESTNET.chainName,
      rpcUrls: [...BSC_TESTNET.rpcUrls],
      blockExplorerUrls: [BSC_TESTNET.explorerUrl],
      nativeCurrency: BSC_TESTNET.nativeCurrency,
    }],
  });

  // Refresh the RPC list even when chain 97 already exists in MetaMask.
  // Otherwise MetaMask keeps using the previously saved endpoint whose
  // circuit breaker may already be open after repeated 5xx/429 responses.
  try {
    await addOrUpdateNetwork();
  } catch (error) {
    if ((error as { code?: number }).code === 4001) throw error;
    // Some injected wallets reject adding an existing chain; switching still
    // works in those wallets, so continue below.
  }

  try {
    await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: BSC_TESTNET.chainIdHex }] });
  } catch (error) {
    if ((error as { code?: number }).code !== 4902) throw error;
    await addOrUpdateNetwork();
    await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: BSC_TESTNET.chainIdHex }] });
  }

}

export function Web3Provider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<string | null>(null);
  const [provider, setProvider] = useState<BrowserProvider | null>(null);
  const [signer, setSigner] = useState<JsonRpcSigner | null>(null);
  const [connecting, setConnecting] = useState(false);
  const connectingRef = useRef(false);

  const connect = useCallback(async () => {
    if (connectingRef.current) return null;
    connectingRef.current = true;
    setConnecting(true);
    try {
      if (!window.ethereum) {
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (isMobile) {
          const dappPath = `${window.location.host}${window.location.pathname}${window.location.search}`;
          toast.info('Opening AETHERA in MetaMask…');
          window.location.href = `https://metamask.app.link/dapp/${dappPath}`;
          return null;
        }
        throw new Error('MetaMask or a compatible wallet is required.');
      }

      // Request wallet permission before chain-management calls. This order is
      // required by several mobile and embedded EIP-1193 wallet providers.
      const permissionProvider = new BrowserProvider(window.ethereum);
      try {
        // MetaMask otherwise commonly returns the account that was authorized
        // previously, even after the user selects another address in the
        // extension. Renewing this permission opens its account selector for
        // an explicit Connect action. Unsupported wallets fall back below.
        await window.ethereum.request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }],
        });
      } catch (permissionError) {
        const code = (permissionError as { code?: number }).code;
        if (code === 4001) throw permissionError;
        // EIP-2255 is optional, so continue with eth_requestAccounts when the
        // connected wallet does not implement wallet_requestPermissions.
      }
      await permissionProvider.send('eth_requestAccounts', []);

      await switchToConfiguredNetwork();

      // Recreate the provider after a chain switch so ethers does not retain
      // the network detected before the wallet changed networks.
      const nextProvider = new BrowserProvider(window.ethereum, 'any');
      const network = await nextProvider.getNetwork();
      if (network.chainId !== BigInt(BSC_TESTNET.chainId)) {
        throw new Error(`Please switch your wallet to ${BSC_TESTNET.chainName} and try again.`);
      }

      const nextSigner = await nextProvider.getSigner();
      const nextAccount = await nextSigner.getAddress();
      setProvider(nextProvider);
      setSigner(nextSigner);
      setAccount(nextAccount);
      localStorage.setItem('aethera_wallet_connected', 'true');
      return nextAccount;
    } catch (error) {
      toast.error(getWeb3Error(error));
      return null;
    } finally {
      connectingRef.current = false;
      setConnecting(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    localStorage.clear();
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Restore an already-authorized wallet silently. Never call connect() on
    // page load because it invokes eth_requestAccounts and chain-management
    // methods, which causes wallet/RPC popups on public and coming-soon pages.
    const restoreConnection = async () => {
      if (localStorage.getItem('aethera_wallet_connected') !== 'true' || !window.ethereum) return;
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' }) as string[];
        if (cancelled || !accounts?.length) return;

        const restoredProvider = new BrowserProvider(window.ethereum, 'any');
        const network = await restoredProvider.getNetwork();
        // Do not prompt to switch networks during automatic restoration. The
        // explicit Connect button handles switching when the user requests it.
        if (cancelled || network.chainId !== BigInt(BSC_TESTNET.chainId)) return;

        const restoredSigner = await restoredProvider.getSigner(accounts[0]);
        if (cancelled) return;
        setProvider(restoredProvider);
        setSigner(restoredSigner);
        setAccount(await restoredSigner.getAddress());
      } catch {
        // Silent restoration is best-effort; explicit Connect remains usable.
      }
    };

    void restoreConnection();
    const handleAccountsChanged = (...args: unknown[]) => {
      const accounts = args[0] as string[] | undefined;
      if (!accounts?.length) {
        setAccount(null);
        setProvider(null);
        setSigner(null);
        localStorage.removeItem('aethera_wallet_connected');
        return;
      }

      void (async () => {
        try {
          const nextProvider = new BrowserProvider(window.ethereum!, 'any');
          const network = await nextProvider.getNetwork();
          if (network.chainId !== BigInt(BSC_TESTNET.chainId)) {
            setAccount(null);
            setProvider(null);
            setSigner(null);
            return;
          }
          const nextSigner = await nextProvider.getSigner(accounts[0]);
          const nextAccount = await nextSigner.getAddress();
          setProvider(nextProvider);
          setSigner(nextSigner);
          setAccount(nextAccount);
          localStorage.setItem('aethera_wallet_connected', 'true');
          window.dispatchEvent(new Event('aethera:updated'));
        } catch {
          setAccount(null);
          setProvider(null);
          setSigner(null);
        }
      })();
    };
    // A chain switch initiated by connect() must not reload the page before
    // connect() can return the selected account to the Launch DApp handler.
    const reload = () => {
      if (!connectingRef.current) window.location.reload();
    };
    window.ethereum?.on?.('accountsChanged', handleAccountsChanged);
    window.ethereum?.on?.('chainChanged', reload);
    return () => {
      cancelled = true;
      window.ethereum?.removeListener?.('accountsChanged', handleAccountsChanged);
      window.ethereum?.removeListener?.('chainChanged', reload);
    };
  }, []);

  const getProvider = useCallback(async () => {
    if (provider) return provider;
    if (!window.ethereum) throw new Error('MetaMask or a compatible wallet is required.');
    return new BrowserProvider(window.ethereum);
  }, [provider]);

  const miningContract = useCallback(async (withSigner = false) => {
    const runner = withSigner ? signer ?? await (await getProvider()).getSigner() : getReadProvider();
    return new Contract(CONTRACTS.mining, AETHERA_ABI, runner);
  }, [getProvider, signer]);

  const tokenContract = useCallback(async (address: string, withSigner = false) => {
    const runner = withSigner ? signer ?? await (await getProvider()).getSigner() : getReadProvider();
    return new Contract(address, ERC20_ABI, runner);
  }, [getProvider, signer]);

  const signMessage = useCallback(async (message: string) => {
    const activeSigner = signer ?? await (await getProvider()).getSigner();
    return activeSigner.signMessage(message);
  }, [getProvider, signer]);

  const sendTransaction = useCallback(async (label: string, action: (contract: Contract) => Promise<PendingTransaction>) => {
    window.dispatchEvent(new CustomEvent('aethera:transaction-loading', { detail: { loading: true } }));
    try {
      if (!account && !(await connect())) return null;
      const tx = await action(await miningContract(true));
      toast.loading(`${label} submitted`, { id: tx.hash, description: 'Waiting for confirmation…' });
      const receipt = await tx.wait();
      if (receipt) await waitForReadProviderBlock(receipt.blockNumber);
      if (receipt) {
        try {
          await persistConfirmedTransaction(receipt.hash, label);
        } catch (databaseError) {
          toast.warning('Transaction confirmed, but database storage failed.', {
            description: databaseError instanceof Error ? databaseError.message : undefined,
          });
        }
      }
      window.dispatchEvent(new Event('aethera:updated'));
      // Refresh once more after short-lived RPC caches have expired.
      window.setTimeout(() => window.dispatchEvent(new Event('aethera:updated')), TIME.transactionRefreshDelayMs);
      toast.success(`${label} confirmed`, {
        id: tx.hash,
        action: { label: 'View', onClick: () => window.open(`${BSC_TESTNET.explorerUrl}/tx/${tx.hash}`, '_blank') },
      });
      return receipt;
    } catch (error) {
      toast.error(getWeb3Error(error));
      return null;
    } finally {
      window.dispatchEvent(new CustomEvent('aethera:transaction-loading', { detail: { loading: false } }));
    }
  }, [account, connect, miningContract]);

  const value = useMemo<Web3ContextValue>(() => ({
    account,
    connected: Boolean(account),
    connecting,
    connect,
    disconnect,
    signMessage,
    miningContract,
    tokenContract,
    sendTransaction,
    formatToken: (amount, decimals = TOKEN_DECIMALS) => formatUnits(amount, decimals),
    parseToken: (amount, decimals = TOKEN_DECIMALS) => parseUnits(amount, decimals),
  }), [account, connect, connecting, disconnect, miningContract, sendTransaction, signMessage, tokenContract]);

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
}

export function useWeb3() {
  const value = useContext(Web3Context);
  if (!value) throw new Error('useWeb3 must be used inside Web3Provider.');
  return value;
}
